import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteScroll } from './useInfiniteScroll';
import type { RowTone } from '../components/game-data/GameDataGrid';

/** Ответ страничного эндпоинта игровых данных. */
export interface GameDataPageResult<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
}

export interface UseGameDataTableOptions<T extends { id: number }> {
  /** Загрузка страницы. Фильтры и сортировку захватывает вызывающий код. */
  load: (page: number) => Promise<GameDataPageResult<T>>;
  /**
   * Значения, при смене которых список перезагружается с нуля (фильтры, сортировка, размер страницы).
   * Ровно как массив зависимостей у `useEffect`.
   */
  deps: unknown[];
  /** Куда отдать текст ошибки загрузки. */
  onError: (error: unknown) => void;
}

export interface UseGameDataTableResult<T extends { id: number }> {
  rows: T[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  /** Callback-ref для скролл-контейнера грида. */
  scrollRef: (node: HTMLDivElement | null) => void;
  /** Перечитать список с первой страницы — кнопка «Обновить» и после правок. */
  reload: () => void;
  /** Заменить строки по id (после сохранения одной строки или массовой правки). */
  patchRows: (updated: T[]) => void;
  /** Убрать строки по id (после удаления). */
  removeRows: (ids: number[]) => void;
  /** Есть ли ещё страницы. */
  hasMore: boolean;
  /** Дочитать следующую страницу — то же, что делает подгрузка по скроллу. */
  loadMore: () => void;

  selectedIds: Set<number>;
  toggleRow: (id: number) => void;
  /** Выделить/снять всё, что уже загружено. */
  toggleAll: () => void;
  clearSelection: () => void;
  setSelection: (ids: number[]) => void;

  tones: Map<number, RowTone>;
  /** Подсветить строки. `ttlMs` — через сколько подсветка сама погаснет. */
  markTone: (ids: number[], tone: RowTone, ttlMs?: number) => void;
  clearTones: () => void;
}

/**
 * Список + выделение + подсветка для редакторов игровых данных.
 *
 * Автообновления здесь нет намеренно: список освежает человек кнопкой «Обновить». От правки
 * поверх устаревших данных защищает не опрос сервера, а `version` — устаревшую строку бэк
 * отклонит с 409, и правки сольёт перехватчик (`api/game-data/conflict-interceptor.ts`).
 *
 * Снимок строки (`base`) для мёрджа берёт форма в момент открытия и держит его в ref —
 * отдельно от полей формы, чтобы сброс формы его не затёр.
 */
export function useGameDataTable<T extends { id: number }>({
  load,
  deps,
  onError,
}: UseGameDataTableOptions<T>): UseGameDataTableResult<T> {
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [tones, setTones] = useState<Map<number, RowTone>>(new Map());

  const loadRef = useRef(load);
  loadRef.current = load;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const toneTimers = useRef<number[]>([]);

  const loadPage = useCallback(async (page: number, append: boolean) => {
    if (!append) setLoading(true);
    setLoadingMore(true);
    try {
      const data = await loadRef.current(page);
      setRows((prev) => (append ? [...prev, ...data.content] : data.content));
      setTotal(data.totalElements);
      setHasMore(page < data.totalPages - 1);
      setPage(page);
    } catch (error) {
      onErrorRef.current(error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    scrollRef,
    loadingMore,
    hasMore,
    page: scrollPage,
    setLoadingMore,
    setHasMore,
    setPage,
    reset: resetScroll,
  } = useInfiniteScroll(() => loadPage(scrollPage + 1, true), { threshold: 100 });

  const reload = useCallback(() => {
    resetScroll();
    loadPage(0, false);
  }, [resetScroll, loadPage]);

  const loadMore = useCallback(() => {
    loadPage(scrollPage + 1, true);
  }, [loadPage, scrollPage]);

  // Смена фильтров/сортировки — список с нуля. Выделение при этом теряет смысл:
  // человек больше не видит те строки и не может отвечать за массовую правку над ними.
  useEffect(() => {
    setSelectedIds(new Set());
    // Старые строки убираем сразу: иначе, пока идёт запрос, они перерисуются под новые колонки —
    // лишняя тяжёлая отрисовка, и на миг видны строки не той категории
    setRows([]);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(
    () => () => {
      toneTimers.current.forEach((timer) => window.clearTimeout(timer));
    },
    [],
  );

  const patchRows = useCallback((updated: T[]) => {
    if (updated.length === 0) return;
    const byId = new Map(updated.map((row) => [row.id, row]));
    setRows((prev) => prev.map((row) => byId.get(row.id) ?? row));
  }, []);

  const removeRows = useCallback((ids: number[]) => {
    const removed = new Set(ids);
    setRows((prev) => prev.filter((row) => !removed.has(row.id)));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    setTotal((prev) => Math.max(0, prev - ids.length));
  }, []);

  const toggleRow = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected = rows.length > 0 && rows.every((row) => prev.has(row.id));
      return allSelected ? new Set<number>() : new Set(rows.map((row) => row.id));
    });
  }, [rows]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const setSelection = useCallback((ids: number[]) => setSelectedIds(new Set(ids)), []);

  const clearTones = useCallback(() => setTones(new Map()), []);

  const markTone = useCallback((ids: number[], tone: RowTone, ttlMs?: number) => {
    setTones((prev) => {
      const next = new Map(prev);
      ids.forEach((id) => next.set(id, tone));
      return next;
    });
    if (!ttlMs) return;
    const timer = window.setTimeout(() => {
      setTones((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => {
          if (next.get(id) === tone) next.delete(id);
        });
        return next;
      });
    }, ttlMs);
    toneTimers.current.push(timer);
  }, []);

  return useMemo(
    () => ({
      rows,
      total,
      loading,
      loadingMore,
      scrollRef,
      reload,
      patchRows,
      removeRows,
      hasMore,
      loadMore,
      selectedIds,
      toggleRow,
      toggleAll,
      clearSelection,
      setSelection,
      tones,
      markTone,
      clearTones,
    }),
    [
      rows,
      total,
      loading,
      loadingMore,
      scrollRef,
      reload,
      patchRows,
      removeRows,
      hasMore,
      loadMore,
      selectedIds,
      toggleRow,
      toggleAll,
      clearSelection,
      setSelection,
      tones,
      markTone,
      clearTones,
    ],
  );
}

export default useGameDataTable;
